import { useEffect, useRef, useId, type ReactNode } from "react";
import type { FieldError } from "./api.ts";
import type { Explanation, ExplainedPredicate } from "./types.ts";
import { metricLabels, operatorLabels, reasonLabel } from "./types.ts";
export function TriggerIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      aria-hidden="true"
    >
      <path d="m13 2-9 12h7l-1 8 10-12h-7l1-8Z" />
    </svg>
  );
}
export function Field({
  id,
  label,
  error,
  hint,
  children,
}: {
  id: string;
  label: string;
  error?: string;
  hint?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="trigger-field">
      <label htmlFor={id}>{label}</label>
      {children}
      {hint && <small id={id + "-hint"}>{hint}</small>}
      {error && (
        <small id={id + "-error"} className="trigger-error">
          {error}
        </small>
      )}
    </div>
  );
}
export function ErrorSummary({
  message,
  errors = [],
  focus = false,
}: {
  message: string;
  errors?: FieldError[];
  focus?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (focus && message) ref.current?.focus();
  }, [focus, message, errors]);
  if (!message) return null;
  return (
    <div className="trigger-alert" role="alert" tabIndex={-1} ref={ref}>
      <strong>{message}</strong>
      {errors.length > 0 && (
        <ul>
          {errors.map((e, i) => (
            <li key={i}>
              <a
                href={"#" + e.path}
                onClick={(event) => {
                  const target = document.getElementById(e.path);
                  if (target) {
                    event.preventDefault();
                    let ancestor = target.parentElement;
                    while (ancestor) {
                      if (ancestor instanceof HTMLDetailsElement)
                        ancestor.open = true;
                      ancestor = ancestor.parentElement;
                    }
                    target.focus();
                    target.scrollIntoView({ block: "nearest" });
                  }
                }}
              >
                {e.message}
              </a>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
export function LoadState({
  loading,
  error,
  empty,
  onRetry,
}: {
  loading: boolean;
  error: string;
  empty: boolean;
  onRetry: () => void;
}) {
  if (error)
    return (
      <div className="trigger-empty" role="alert">
        <p>{error}</p>
        <button className="btn btn--secondary" onClick={onRetry}>
          Спробувати ще раз
        </button>
      </div>
    );
  if (loading && empty)
    return (
      <div role="status" className="trigger-loading">
        Завантаження…
      </div>
    );
  return null;
}
export function LogicControl({
  value,
  onChange,
  label,
}: {
  value: "all" | "any";
  onChange: (v: "all" | "any") => void;
  label: string;
}) {
  const name = useId();
  return (
    <fieldset className="trigger-logic">
      <legend>{label}</legend>
      {(["all", "any"] as const).map((v) => (
        <label key={v}>
          <input
            type="radio"
            name={name}
            checked={v === value}
            onChange={() => onChange(v)}
          />
          {v === "all" ? "Усі (І)" : "Хоча б одна (АБО)"}
        </label>
      ))}
    </fieldset>
  );
}
export const truthLabel = (v: boolean | null) =>
  v === null ? "Невідомо" : v ? "Збіг" : "Не збігається";
function PredicateEvidence({
  predicate: p,
}: {
  predicate: ExplainedPredicate;
}) {
  return (
    <div className="trigger-evidence-row">
      <span>{metricLabels[p.metric]}</span>
      <strong>
        {p.comparedActual ?? "Невідомо"}{" "}
        {
          operatorLabels[p.operator as keyof typeof operatorLabels]?.split(
            " — ",
          )[0]
        }{" "}
        {p.comparedThreshold}
      </strong>
      <span
        className={
          "trigger-status " +
          (p.result === null ? "is-unknown" : p.result ? "is-match" : "")
        }
      >
        {truthLabel(p.result)}
      </span>
    </div>
  );
}
export function ExplanationTree({ value }: { value: Explanation }) {
  return (
    <div className="trigger-explanation">
      <PredicateEvidence predicate={value.event} />
      {value.conditions && (
        <>
          <p>
            Між блоками:{" "}
            {value.conditions.operator === "all"
              ? "усі (І)"
              : "хоча б один (АБО)"}
          </p>
          {value.conditions.blocks.map((b, i) => (
            <div key={b.id}>
              <h4>
                Блок {i + 1} · {b.operator === "all" ? "усі" : "хоча б одна"}
              </h4>
              {b.predicates.map((p) => (
                <PredicateEvidence key={p.id} predicate={p} />
              ))}
            </div>
          ))}
        </>
      )}
    </div>
  );
}
export function Reasons({ values }: { values: string[] }) {
  return values.length ? (
    <ul className="trigger-reasons">
      {[...new Set(values)].map((r) => (
        <li key={r}>{reasonLabel(r)}</li>
      ))}
    </ul>
  ) : null;
}
